import { useState, useCallback, useRef } from 'react';
import { loadFFmpeg, convertToMp3, convertAudio, isFFmpegLoaded } from '@/lib/ffmpeg';
import { AudioExportOptions } from '@/types/recording';

export type FFmpegStatus = 'idle' | 'loading' | 'ready' | 'converting' | 'done' | 'error';

export function useFFmpeg() {
  const [status, setStatus] = useState<FFmpegStatus>(
    isFFmpegLoaded() ? 'ready' : 'idle'
  );
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const resultRef = useRef<Blob | null>(null);

  // 預載入 FFmpeg
  const preload = useCallback(async () => {
    if (isFFmpegLoaded()) {
      setStatus('ready');
      return;
    }

    try {
      setStatus('loading');
      setProgress(0);
      console.log('Loading FFmpeg...');
      await loadFFmpeg((p) => setProgress(p));
      console.log('FFmpeg loaded successfully');
      setStatus('ready');
    } catch (err) {
      console.error('FFmpeg load failed:', err);
      setError(err instanceof Error ? err.message : '載入 FFmpeg 失敗');
      setStatus('error');
    }
  }, []);

  // 轉換音頻（支援 options 參數和進度回調）
  const convert = useCallback(async (
    webmBlob: Blob,
    options?: AudioExportOptions,
    onProgress?: (progress: number) => void
  ): Promise<Blob | null> => {
    try {
      // 如果沒有 options 或格式為 webm，直接返回原始 blob
      if (!options || options.format === 'webm') {
        onProgress?.(100);
        return webmBlob;
      }

      // 確保已載入
      if (!isFFmpegLoaded()) {
        console.log('FFmpeg not loaded, loading now...');
        setStatus('loading');
        await loadFFmpeg((p) => {
          setProgress(p);
          onProgress?.(p);
        });
      }

      setStatus('converting');
      setProgress(0);
      onProgress?.(0);
      setError(null);

      // 使用通用轉換函數
      console.log(`Starting conversion: ${options.format} (${options.quality})`);
      const convertedBlob = await convertAudio(
        webmBlob,
        { format: options.format, quality: options.quality },
        (p) => {
          setProgress(p);
          onProgress?.(p);
        }
      );

      resultRef.current = convertedBlob;
      setStatus('done');
      console.log('Conversion successful');
      return convertedBlob;
    } catch (err) {
      console.error('FFmpeg conversion failed:', err);
      setError(err instanceof Error ? err.message : '轉換失敗');
      setStatus('error');
      return null;
    }
  }, []);

  // 重置狀態
  const reset = useCallback(() => {
    setStatus(isFFmpegLoaded() ? 'ready' : 'idle');
    setProgress(0);
    setError(null);
    resultRef.current = null;
  }, []);

  return {
    status,
    progress,
    error,
    result: resultRef.current,
    preload,
    convert,
    reset,
    isReady: status === 'ready' || status === 'done',
  };
}
